import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { downloadFile, getPresignedUrl } from '../../../Company/UploadJourney/components/UploadCard/utils/api/helpers';
import { regenerateAuditedReport } from '../../../../store/dashboard/auditedFinancials/service';
import { AUDITED_REGENERATE_FAILED_TOAST } from '../../../../store/dashboard/auditedFinancials/constants';
import { Mixpanel } from '../../../../utils/mixpanel';
import { AICA_AUDITED_FINANCIALS_ACTION } from '../../../../utils/constants/mixpanelEvents/investorEvents';

export { AUDITED_REGENERATE_FAILED_TOAST } from '../../../../store/dashboard/auditedFinancials/constants';

interface UseAuditedReportDownloadProps {
    assesseeOrgId: string;
    orgName?: string;
}

interface ViewerState {
    url: string;
    mimeType: string;
    docId: string;
    fileName: string;
}

interface UseAuditedReportDownloadResult {
    downloading: boolean;
    viewLoading: boolean;
    viewerState: ViewerState | null;
    onViewClick: () => Promise<void>;
    onDownloadClick: () => Promise<void>;
    closeViewer: () => void;
}

/**
 * Derives the save-as filename from the regenerate response.
 * Strips filesystem-hostile characters from orgName, then inserts it before
 * the extension: "<baseName> - <orgName><ext>". Falls back to the bare
 * resolvedFileName when orgName is empty.
 */
function buildSaveAsName(resolvedFileName: string, orgName: string): string {
    const sanitizedOrgName = orgName.replace(/[\\/:*?"<>|]/g, '');
    if (!sanitizedOrgName) return resolvedFileName;

    const lastDot = resolvedFileName.lastIndexOf('.');
    const base = lastDot !== -1 ? resolvedFileName.slice(0, lastDot) : resolvedFileName;
    const ext = lastDot !== -1 ? resolvedFileName.slice(lastDot) : '';
    return `${base} - ${sanitizedOrgName}${ext}`;
}

export function useAuditedReportDownload({ assesseeOrgId, orgName = '' }: UseAuditedReportDownloadProps): UseAuditedReportDownloadResult {
    const inFlightRef = useRef(false);
    const [downloading, setDownloading] = useState(false);
    const [viewLoading, setViewLoading] = useState(false);
    const [viewerState, setViewerState] = useState<ViewerState | null>(null);

    const regenerateAndGetDoc = async (): Promise<{ docId: string; fileName: string } | null> => {
        const response = await regenerateAuditedReport(assesseeOrgId);
        const docId: string | undefined = response?.data?.data?.docId;
        const fileName: string | undefined = response?.data?.data?.fileName;

        if (!docId) {
            const serverMsg = response?.data?.responseData?.responseMessage;
            toast.error(serverMsg || AUDITED_REGENERATE_FAILED_TOAST);
            return null;
        }

        if (Number.isNaN(Number(docId))) {
            toast.error(AUDITED_REGENERATE_FAILED_TOAST);
            return null;
        }

        return { docId, fileName: fileName ?? docId };
    };

    const onViewClick = async () => {
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        setViewLoading(true);
        try {
            const doc = await regenerateAndGetDoc();
            if (!doc) return;

            Mixpanel.track(AICA_AUDITED_FINANCIALS_ACTION, { action: 'regenerate' });

            const presigned = await getPresignedUrl({ docId: Number(doc.docId) }, false);
            if (!presigned?.uri) {
                toast.error(AUDITED_REGENERATE_FAILED_TOAST);
                return;
            }

            setViewerState({
                url: presigned.uri,
                mimeType: presigned.fileType || 'application/pdf',
                docId: doc.docId,
                fileName: doc.fileName,
            });
        } catch (err) {
            console.error('Failed to load audited report for view:', err);
            toast.error(AUDITED_REGENERATE_FAILED_TOAST);
        } finally {
            inFlightRef.current = false;
            setViewLoading(false);
        }
    };

    const onDownloadClick = async () => {
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        setDownloading(true);
        try {
            const doc = await regenerateAndGetDoc();
            if (!doc) return;

            Mixpanel.track(AICA_AUDITED_FINANCIALS_ACTION, { action: 'download' });

            const saveAsName = buildSaveAsName(doc.fileName, orgName);
            await downloadFile(doc.docId, undefined, saveAsName);
        } catch (err) {
            console.error('Failed to regenerate audited report:', err);
            toast.error(AUDITED_REGENERATE_FAILED_TOAST);
        } finally {
            inFlightRef.current = false;
            setDownloading(false);
        }
    };

    const closeViewer = () => {
        setViewerState(null);
    };

    return { downloading, viewLoading, viewerState, onViewClick, onDownloadClick, closeViewer };
}
